import { FC, useState } from "react";
import { Theme } from "../../../types";
import clsx from "clsx";
import { useTranslations } from "use-intl";
import { useStoreState } from "../../store/useStore";
import {
  megaMenuTabBar,
  megaMenuTabBase,
  megaMenuTabActive,
  megaMenuTabInactive,
  megaMenuHoverRow,
  megaMenuLinkTitle,
  megaMenuItemDesc
} from "../../styles";

interface BotProductsWidgetProps {
  theme: Theme;
  defaultTab?: BotTab;
}

type BotTab = "futures" | "spot";

interface BotProduct {
  /** i18n key for the name */
  nameKey: string;
  /** i18n key for the description (tags / features) */
  descKey: string;
  /** 跳转路径（相对），保持改版前 trading tools 入口形态 */
  url: string;
}

/**
 * Bot 产品配置
 * 匹配 Figma 设计稿：Futures Tab / Spot Tab 下的 Bot 产品列表
 */
const BOT_PRODUCTS: Record<BotTab, BotProduct[]> = {
  futures: [
    {
      nameKey: "botSignalBot",
      descKey: "botSignalBotDes",
      url: "BTC-USDT?mode=tools&strategy=signalBot"
    },
    {
      nameKey: "botFuturesGrid",
      descKey: "botFuturesGridDes",
      url: "BTC-USDT?mode=tools&strategy=futuresGrid"
    },
    {
      nameKey: "botFuturesDCA",
      descKey: "botFuturesDCADes",
      url: "BTC-USDT?mode=tools&strategy=futuresDca"
    }
  ],
  spot: [
    {
      nameKey: "botSpotGrid",
      descKey: "botSpotGridDes",
      url: "BTC-USDT?mode=tools&strategy=spotGrid"
    },
    {
      nameKey: "botSpotDCA",
      descKey: "botSpotDCADes",
      url: "BTC-USDT?mode=tools&strategy=spotDca"
    }
  ]
};

/**
 * Bot 产品名称的 fallback（当翻译 key 缺失时显示）
 */
const BOT_NAME_FALLBACK: Record<string, string> = {
  botSignalBot: "Signal Bot",
  botFuturesGrid: "Futures Grid",
  botFuturesDCA: "Futures DCA",
  botSpotGrid: "Spot Grid",
  botSpotDCA: "Spot DCA"
};

const BOT_DESC_FALLBACK: Record<string, string> = {
  botSignalBotDes: "Trade futures automatically with signals from TradingView",
  botFuturesGridDes: "Cyclic arbitrage / Short and medium terms / Leveraged trading",
  botFuturesDCADes: "Buy the dip / Sell the rip / Dollar-Cost averaging / Multiple terms",
  botSpotGridDes: "Buy low sell high / Trade uptrends / Short, medium, and long terms",
  botSpotDCADes: "Triggered by indicators / For volatile markets / Medium and long terms"
};

/**
 * 安全翻译函数：当 use-intl 找不到 key 时，它会返回完整路径（如 "header.xxx"），
 * 这是一个 truthy 字符串，导致 `||` fallback 永远不触发。
 * 此函数检测 missing key 并正确返回 fallback。
 */
const safeTranslate = (
  t: ReturnType<typeof useTranslations<"header">>,
  key: string,
  fallback: string
): string => {
  try {
    const result = t(key as any);
    // use-intl 在 key 缺失时返回 "header.<key>" 或 key 本身
    if (!result || result === key || result === `header.${key}`) {
      return fallback;
    }
    return result;
  } catch {
    return fallback;
  }
};

/**
 * BotProductsWidget — Bot 产品列表面板
 *
 * 匹配 Figma 设计稿 13923:3272 / 13935:2432：
 * - Futures / Spot Tab 切换
 * - 每个 Tab 下展示对应 Bot 产品列表
 * - 第一个产品默认高亮
 */
const BotProductsWidget: FC<BotProductsWidgetProps> = ({ theme, defaultTab = "futures" }) => {
  const t = useTranslations("header");
  const { localeLink } = useStoreState();
  const [activeTab, setActiveTab] = useState<BotTab>(defaultTab);

  const products = BOT_PRODUCTS[activeTab];

  const handleNavigate = (product: BotProduct) => {
    const base = activeTab === "futures" ? "futures" : "spot";
    window.location.href = `/${base}${localeLink}/${product.url}`;
  };

  return (
    <div className="flex flex-col gap-[8px] w-full h-full">
      {/* Tab 栏：Futures / Spot（shrink-0 防止被压缩，px-[8px] 与 SearchMarketWidget 一致） */}
      <div className="shrink-0 px-[8px]">
        <BotTabBar theme={theme} activeTab={activeTab} onChange={setActiveTab} t={t} />
      </div>

      {/* 产品列表（flex-1 填满剩余空间） */}
      <div className="flex flex-col flex-1 min-h-0">
        {products.map((product) => (
          <BotProductRow
            key={product.nameKey}
            product={product}
            theme={theme}
            onClick={() => handleNavigate(product)}
            t={t}
          />
        ))}
      </div>
    </div>
  );
};

// ─── Bot Tab Bar ──────────────────────────────────────────────

const BotTabBar: FC<{
  theme: Theme;
  activeTab: BotTab;
  onChange: (tab: BotTab) => void;
  t: ReturnType<typeof useTranslations<"header">>;
}> = ({ theme, activeTab, onChange, t }) => {
  const tabs: { key: BotTab; label: string }[] = [
    { key: "futures", label: t("futures" as any) || "Futures" },
    { key: "spot", label: t("spot" as any) || "Spot" }
  ];

  return (
    <div className={megaMenuTabBar({ mode: theme })}>
      {tabs.map((tab) => {
        const isActive = activeTab === tab.key;
        return (
          <button
            key={tab.key}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange(tab.key);
            }}
            className={clsx(
              megaMenuTabBase(),
              isActive ? megaMenuTabActive({ mode: theme }) : megaMenuTabInactive({ mode: theme })
            )}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
};

// ─── Bot Product Row ──────────────────────────────────────────

const BotProductRow: FC<{
  product: BotProduct;
  theme: Theme;
  onClick: () => void;
  t: ReturnType<typeof useTranslations<"header">>;
}> = ({ product, theme, onClick, t }) => {
  const name = safeTranslate(
    t,
    product.nameKey,
    BOT_NAME_FALLBACK[product.nameKey] || product.nameKey
  );
  const desc = safeTranslate(
    t,
    product.descKey,
    BOT_DESC_FALLBACK[product.descKey] || product.descKey
  );

  return (
    <div
      className={clsx("group flex flex-col gap-[4px]", megaMenuHoverRow({ mode: theme }))}
      onClick={onClick}
    >
      {/* 产品名称：hover 时变为主题色 */}
      <span
        className={clsx(
          "text-[14px] font-medium leading-[20px]",
          megaMenuLinkTitle({ mode: theme })
        )}
      >
        {name}
      </span>

      {/* 产品描述（tags / features） */}
      <span className={megaMenuItemDesc({ mode: theme })}>{desc}</span>
    </div>
  );
};

export default BotProductsWidget;
